import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Button,
  CssBaseline,
  useTheme,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import darkTheme from "./theme/theme";

import CreateAccount from "./CreateUser";
import ViewUsers from "./ViewUsers";
import ManageLogs from "./ManageLogs";
import AdminAnalyticsDashboard from "./AdminAnalyticsDashboard";
import UserAnalyticsDashboard from "./user_analytics/UserAnalyticsDashboard";
import AdminFileManager from "./file_management/AdminFileManager";
import { handleSignOut } from "./utils/signoutUtils";
import FileAnalyticsDashboard from "./file_management/file_analytics/FileAnalyticsDashboard";

import CategoryFolderList from "./model_dataset/component/CategoryFolderList";
import AdminDrawer from "./AdminDrawer";

import { useAuth } from "../../context/AuthContext";
import { fetchAllFilesRecursively } from "./file_management/hooks/useFileAnalyticFetch";
import { useQuery } from "@tanstack/react-query";
import { getUserField } from "../../shared/services/fetchUserField";

function AdminDashboardContent() {
  const theme = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();
  const [selectedMenu, setSelectedMenu] = useState("analyticsDashboard");
  const [userMgmtOpen, setUserMgmtOpen] = useState(true);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [fileMgmtOpen, setFileMgmtOpen] = useState(true);
  const [modelMgmtOpen, setModelMgmtOpen] = useState(false);
  const [departmentId, setDepartmentId] = useState(null);

  const userId = currentUser ? currentUser.uid : null;

  useEffect(() => {
    async function fetchDepartment() {
      if (userId) {
        const dep = await getUserField(userId, "department");
        setDepartmentId(dep);
      }
    }
    fetchDepartment();
  }, [userId]);

  const handleNavigate = (menu) => {
    setSelectedMenu(menu);
  };

  const navigateToLogin = () => {
    navigate("/login");
  };

  const onSignOutClick = () => {
    handleSignOut(navigateToLogin);
  };

  console.log(
    "[DEBUG][ADMIN][TITLE] AdminDashboard.js -> useFileAnalyticFetch"
  );
  console.log("[DEBUG][ADMIN] userId(Admin) -> ", currentUser.uid);

  useEffect(() => {
    if (currentUser?.uid) {
      queryClient.prefetchQuery({
        queryKey: ["fileAnalyticsApprovedFiles", currentUser.uid],
        queryFn: () => fetchAllFilesRecursively(currentUser.uid),
        staleTime: 1000 * 60 * 5, // 5 minutes, adjust as needed
      });
    }
  }, [currentUser?.uid, queryClient]);

  const renderContent = () => {
    switch (selectedMenu) {
      case "analyticsDashboard":
      case "filesAnalytics":
        return <FileAnalyticsDashboard />;
      case "userAnalytics":
        return <UserAnalyticsDashboard />;
      case "createAccount":
        return <CreateAccount />;
      case "viewUsers":
        return <ViewUsers />;
      case "pendingSubmissions":
        return (
          <AdminFileManager
            selectedStatus="pending"
            onNavigate={handleNavigate}
          />
        );
      case "approvedSubmissions":
        return (
          <AdminFileManager
            selectedStatus="approved"
            onNavigate={handleNavigate}
          />
        );
      case "userLogs":
        return <ManageLogs />;
      case "model_dataset":
        return <CategoryFolderList departmentId={departmentId} />;
      default:
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h4">Welcome to the Admin Dashboard</Typography>
            <Typography sx={{ mt: 2 }} color="text.secondary">
              Please select an option from the menu.
            </Typography>
          </Box>
        );
    }
  };

  return (
    <Box sx={{ display: "flex", background: "#000" }}>
      <CssBaseline />
      {/* AppBar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: "#000",
          color: theme.palette.text.primary,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Toolbar>
          <Typography variant="h6" noWrap sx={{ flexGrow: 1, fontWeight: 700 }}>
            Detect-Classify-Review Admin
          </Typography>
          <Button variant="outlined" onClick={onSignOutClick}>
            SIGN OUT
          </Button>
        </Toolbar>
      </AppBar>
      {/* Drawer */}
      <AdminDrawer
        selectedMenu={selectedMenu}
        setSelectedMenu={setSelectedMenu}
        userMgmtOpen={userMgmtOpen}
        setUserMgmtOpen={setUserMgmtOpen}
        analyticsOpen={analyticsOpen}
        setAnalyticsOpen={setAnalyticsOpen}
        fileMgmtOpen={fileMgmtOpen}
        setFileMgmtOpen={setFileMgmtOpen}
        modelMgmtOpen={modelMgmtOpen}
        setModelMgmtOpen={setModelMgmtOpen}
      />
      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 4,
          background: "#000",
          color: theme.palette.text.primary,
          minHeight: "100vh",
        }}
      >
        <Toolbar />
        {renderContent()}
      </Box>
    </Box>
  );
}

export default function AdminDashboard() {
  return (
    <ThemeProvider theme={darkTheme}>
      <AdminDashboardContent />
    </ThemeProvider>
  );
}
